import * as Yup from "yup";
import { CONTAINER_TYPE_VALUES } from "../constants/containerOrderConstants";
import {
  dayOf,
  findRouteSequenceViolations,
  timeOf,
} from "../utils/routeSequence";
import i18n from "../i18n/i18n";
import { translateValidation } from "../utils/translateValidation";
import { optionalPhoneSchema } from "../utils/phone";

/**
 * Container order validation.
 *
 * The bar is deliberately "enough to generate a usable container shipment",
 * not "every field filled". A generated shipment must satisfy the shipment
 * Save validator, which requires a client company name plus a city on both
 * pickup and delivery — those map to the order's client and to stops 1 and 3.
 * On top of that a container batch is meaningless without a container type and
 * a loading site, so those are required too, and each of the three stops must
 * carry a real, located address (see `stopSchema`).
 *
 * Every company on the order — the client and all three stops — must also be a
 * directory record rather than typed text (see `requiredId`).
 *
 * Everything else (booking number, incoterm, date range, vessel, tare, the per
 * stop date/time, ref and PIN, ramp, contact, cut-off) stays optional — it is
 * reference data that does not block dispatching a container.
 */

const requiredText = (message: string) =>
  Yup.string()
    .transform((value) => (typeof value === "string" ? value.trim() : value))
    .required(message);

/**
 * The link to the directory record a name was picked from.
 *
 * Required, and that is the whole point: an order is the template a batch of
 * shipments and their invoices is generated from, so every company on it has to
 * be a record the system knows — not a name someone typed. The form only ever
 * writes this id from a picked suggestion (see DirectorySelect), so a present
 * id is proof that the name, address and map location beside it came from the
 * directory together.
 *
 * Orders saved before this rule have no ids; editing one requires re-picking
 * its companies, which backfills them. Same "heal on touch" approach as the
 * stop coordinates below, and the API enforces the same rule.
 */
const requiredId = (message: string) =>
  Yup.string()
    .nullable()
    .transform((value) => (value ? String(value) : null))
    .required(message);

/**
 * A stop needs a name to call it by, an address to drive to, and the map
 * location of that address.
 *
 * The coordinates rule is the one the Addresses/Partners directories enforce:
 * they only exist if the address was chosen from the Google Places dropdown (or
 * inherited from a directory record that was). A stop is a dispatch point, so a
 * hand-typed street with no location is not enough — unlike the shipment form,
 * where a draft may still carry a typed address.
 *
 * Orders saved before this existed have no coordinates; editing one requires
 * re-picking its three addresses, which backfills them. Same "heal on touch"
 * approach as the directories, and the API enforces the same rule.
 */
/** The map location rule, which is the one part of a stop that cannot be typed. */
const locationSchema = (message: string) =>
  Yup.object({
    latitude: Yup.number()
      .typeError(message)
      .min(-90)
      .max(90)
      .required(message),
    longitude: Yup.number()
      .typeError(message)
      .min(-180)
      .max(180)
      .required(message),
  })
    // Without this yup casts a missing value to `{}` and reports two child
    // errors instead of one "pick from the suggestions" on the object.
    .default(undefined)
    .required(message);

/**
 * Whether a stop is being made, and why not when it is not.
 *
 * The reason is required whenever the stop is off, with a floor long enough that
 * "n/a" does not pass for an explanation — it travels down to every container
 * generated from the order and onto the driver's screen, and "no pickup" without
 * "the box is already on site" is an instruction nobody can check.
 */
const skipReasonSchema = Yup.string().when("enabled", {
  is: false,
  then: (schema) =>
    schema
      .transform((value) => (typeof value === "string" ? value.trim() : value))
      .min(3, "validation:containerOrder.skipReasonRequired")
      .required("validation:containerOrder.skipReasonRequired"),
  otherwise: (schema) => schema.notRequired(),
});

/**
 * A field that is only required while its stop is being made.
 *
 * Conditioned per field on the sibling `enabled` rather than on the stop as a
 * whole, and that is forced by how yup resolves references: `when` looks at
 * *siblings*, so an object cannot condition itself on one of its own children.
 * Every field here is a direct sibling of `enabled`, which is exactly the shape
 * that works — and it is why `address` is switched as one object rather than
 * street and city being switched individually two levels down.
 *
 * A stop that is off is held to none of this. There is no truck going to it, so
 * demanding a directory record and a map location would be asking the
 * dispatcher to describe a place nobody is driving to before they are allowed to
 * say nobody is driving to it. The fields keep whatever was filled in before,
 * which is what makes turning it back on a click rather than a re-entry.
 */
const whenUsed = <T extends Yup.AnySchema>(strict: T, relaxed: Yup.AnySchema) =>
  strict.when("enabled", {
    is: false,
    then: () => relaxed,
    // Absent means used — every stop saved before they could be turned off.
    otherwise: () => strict,
  });

/** What a relaxed field accepts: anything, including nothing. */
const anyText = Yup.string().nullable().notRequired();
const anyObject = Yup.object().nullable().notRequired();

/**
 * One of the route's three anchors.
 *
 * All three resolve to a record in the Addresses directory — they are places a
 * truck is sent to, which is the one thing the pickup terminal, the client's
 * loading site and the drop-off terminal have in common. Hence `addressId` below
 * rather than a per-stop link field.
 */
const stopSchema = (
  idMessage: string,
  nameMessage: string,
  streetMessage: string,
  cityMessage: string,
  locationMessage: string,
  /**
   * Whether this stop can be dropped from the route at all.
   *
   * The two terminals can — a container is not always collected from one, and
   * not always returned to one. The loading site cannot: it is the work the
   * order exists for, and both the API and the model refuse it.
   */
  skippable = false,
) => {
  const address = Yup.object({
    street: requiredText(streetMessage),
    city: requiredText(cityMessage),
    coordinates: locationSchema(locationMessage),
  });

  if (!skippable) {
    return Yup.object({
      addressId: requiredId(idMessage),
      companyName: requiredText(nameMessage),
      address,

      contactPhone: optionalPhoneSchema(),
    });
  }

  return Yup.object({
    enabled: Yup.boolean(),
    skipReason: skipReasonSchema,
    addressId: whenUsed(requiredId(idMessage), anyText),
    companyName: whenUsed(requiredText(nameMessage), anyText),
    address: whenUsed(address, anyObject),
    contactPhone: optionalPhoneSchema(),
  });
};

/**
 * One stop the order adds between the three it always has.
 *
 * Held to less than an anchor, and deliberately so. An anchor must resolve to a
 * directory record because a whole batch is dispatched to it; an added stop is
 * routinely somewhere no directory holds — a certified scale on an industrial
 * estate, a customs window, a lay-by the tachograph forced. Requiring a record
 * would make the feature unusable for most of what it exists for.
 *
 * What it is still held to is what dispatch actually depends on: a name, a
 * street, a city, and the map location of that street.
 */
const additionalStopSchema = Yup.object({
  enabled: Yup.boolean(),
  skipReason: skipReasonSchema,
  companyName: whenUsed(
    requiredText("validation:containerOrder.stopNameRequired"),
    anyText,
  ),
  address: whenUsed(
    Yup.object({
      street: requiredText("validation:containerOrder.stopAddressRequired"),
      city: requiredText("validation:containerOrder.stopCityRequired"),
      coordinates: locationSchema(
        "validation:containerOrder.stopLocationRequired",
      ),
    }),
    anyObject,
  ),
  contactPhone: optionalPhoneSchema(),
});

/**
 * Where an order keeps each of the route's four moments.
 *
 * The rule itself — all pairs, what a missing time means, why — lives in
 * `utils/routeSequence`, shared with the individual container's page, which
 * inherits these slots and edits them under the same constraint.
 */
/**
 * What each named slot is called in a sentence.
 *
 * Needed because the generic "X is scheduled before Y" message names both ends
 * from their labels, and a slot with no label falls back to its internal name —
 * so an order whose customs stop clashed with the drop-off said "delivery is
 * scheduled before Customs" while the container's own page said "Terminal
 * drop-off is scheduled before Customs - Muuga". Same rule, same function, two
 * different sentences, and the order's was the one written for a programmer.
 *
 * Read through i18n at validation time rather than passed in from the form: a
 * yup schema is not a component and has no `t`, and these four labels are the
 * same ones the route section renders its headings from.
 */
const SLOT_LABEL_KEYS: Record<string, string> = {
  pickup: "containerOrders:route.terminalPickup",
  onSite: "containerOrders:route.loadingSite",
  delivery: "containerOrders:route.terminalDropoff",
  cutOff: "containerOrders:route.cutOffDate",
};

const ROUTE_SEQUENCE = [
  {
    name: "pickup",
    // Which stop's own `enabled` flag decides whether this slot is checked, and
    // which gap the added stops after it fall into. Absent on the cut-off,
    // which is not a stop — it closes the route.
    stopKey: "terminalPickup",
    date: "terminalPickup.pickupDate",
    time: "terminalPickup.pickupTime",
  },
  {
    name: "onSite",
    stopKey: "loadingSite",
    date: "loadingSite.onSiteDate",
    time: "loadingSite.onSiteTime",
  },
  {
    name: "delivery",
    stopKey: "terminalDropoff",
    date: "terminalDropoff.deliveryDate",
    time: "terminalDropoff.deliveryTime",
  },
  {
    name: "cutOff",
    stopKey: undefined,
    date: "terminalDropoff.cutOffDate",
    time: "terminalDropoff.cutOffTime",
  },
] as const;

/** `loadingSite.onSiteDate` → the value at that path within the route. */
const at = (route: unknown, path: string): unknown =>
  path
    .split(".")
    .reduce<unknown>(
      (node, key) =>
        node && typeof node === "object"
          ? (node as Record<string, unknown>)[key]
          : undefined,
      route,
    );

export const containerOrderSchema = Yup.object({
  client: Yup.object({
    partnerId: requiredId("validation:containerOrder.clientSelectionRequired"),
    companyName: requiredText("validation:containerOrder.clientRequired"),
    phoneNumber: optionalPhoneSchema(),
  }),

  direction: Yup.string()
    .oneOf(
      ["Outbound", "Inbound"],
      "validation:containerOrder.directionInvalid",
    )
    .required("validation:containerOrder.directionRequired"),

  containerType: Yup.string()
    .oneOf(
      CONTAINER_TYPE_VALUES,
      "validation:containerOrder.containerTypeInvalid",
    )
    .required("validation:containerOrder.containerTypeRequired"),

  route: Yup.object({
    terminalPickup: stopSchema(
      "validation:containerOrder.pickupTerminalSelectionRequired",
      "validation:containerOrder.pickupTerminalRequired",
      "validation:containerOrder.pickupAddressRequired",
      "validation:containerOrder.pickupCityRequired",
      "validation:containerOrder.pickupLocationRequired",
      true,
    ),
    loadingSite: stopSchema(
      "validation:containerOrder.loadingSiteSelectionRequired",
      "validation:containerOrder.loadingSiteRequired",
      "validation:containerOrder.loadingAddressRequired",
      "validation:containerOrder.loadingCityRequired",
      "validation:containerOrder.loadingLocationRequired",
    ),
    terminalDropoff: stopSchema(
      "validation:containerOrder.dropoffTerminalSelectionRequired",
      "validation:containerOrder.dropoffTerminalRequired",
      "validation:containerOrder.dropoffAddressRequired",
      "validation:containerOrder.dropoffCityRequired",
      "validation:containerOrder.dropoffLocationRequired",
      true,
    ),
    additionalStops: Yup.array().of(additionalStopSchema),
  })
    // A route runs collect → load → return, and the cut-off closes it. Whatever
    // of that is filled in has to read in that order — across every pair; see
    // ROUTE_SEQUENCE.
    .test("route-order", "", function (route) {
      // Reported against whichever half of the slot is at fault — the date field
      // when dates conflict, the time field when times do — so the message lands
      // under the box that has to change. Every violation is reported, not just
      // the first, rather than surfacing them one at a time as they are fixed.
      //
      // The slots are assembled rather than listed, because the route is no
      // longer a fixed four points: a customs stop between the terminal and the
      // client has a slot of its own, and a slot that is not checked is one that
      // can be booked before the stop it follows. Stops that are not being made
      // drop out entirely — there is no truck going to them, so a date left
      // behind on one must not be able to refuse the save.
      const slots: Array<{
        name: string;
        label?: string;
        day: string | null;
        clock: string | null;
        datePath: string;
        timePath: string;
      }> = [];

      const addedIn = (gap: string) =>
        ((route as any)?.additionalStops ?? [])
          .map((stop: any, index: number) => ({ stop, index }))
          .filter(
            ({ stop }: any) =>
              (stop?.after ?? "loadingSite") === gap && stop?.enabled !== false,
          )
          .forEach(({ stop, index }: any) =>
            slots.push({
              name: `added-${index}`,
              // What the dispatcher called it, then who it is, then what it is
              // for — the same three fallbacks the container's own page uses, so
              // one stop is named identically wherever it is complained about.
              // Never left empty: an empty label degrades to the internal slot
              // name, and "added-0 is scheduled before…" is not a sentence.
              label:
                stop?.label?.trim() ||
                stop?.companyName?.trim() ||
                i18n.t(`containerOrders:route.stopKinds.${stop?.kind ?? "other"}`),
              day: dayOf(stop?.date),
              clock: timeOf(stop?.time),
              datePath: `additionalStops.${index}.date`,
              timePath: `additionalStops.${index}.time`,
            }),
          );

      addedIn("start");
      for (const point of ROUTE_SEQUENCE) {
        // The cut-off has no stop of its own — it closes the drop-off — so it
        // is always pushed, and nothing is anchored after it. Its position in
        // this list is what matters: it comes straight after the delivery slot
        // and *before* anything scheduled after the drop-off (an empty-depot
        // return, a mandated rest), because those genuinely happen once the
        // deadline has been met.
        const stop = point.stopKey
          ? (route as any)?.[point.stopKey]
          : undefined;
        if (!point.stopKey || stop?.enabled !== false) {
          slots.push({
            name: point.name,
            // Only read when this slot ends up in the generic sentence — which
            // is exactly when it is paired with an added stop, and exactly when
            // "delivery" would otherwise appear in the message.
            label: i18n.t(SLOT_LABEL_KEYS[point.name]),
            day: dayOf(at(route, point.date)),
            clock: timeOf(at(route, point.time)),
            datePath: point.date,
            timePath: point.time,
          });
        }
        // The drop-off's own gap opens after the cut-off, which is the next
        // entry in ROUTE_SEQUENCE — so it is filled on the following pass.
        if (point.stopKey && point.stopKey !== "terminalDropoff") {
          addedIn(point.stopKey);
        }
        if (point.name === "cutOff") addedIn("terminalDropoff");
      }

      const byName = Object.fromEntries(slots.map((slot) => [slot.name, slot]));

      const failures = findRouteSequenceViolations(slots).map((violation) => {
        const slot = byName[violation.slot.name];
        return this.createError({
          path: `${this.path}.${violation.part === "date" ? slot.datePath : slot.timePath}`,
          /**
           * Translated here, values and all, rather than passed on as a key.
           *
           * A yup error message is a string and nothing else — there is nowhere
           * on it to carry the two stop names the generic sentence needs. So the
           * form received the key, translated it with no values, and rendered
           * "{{later}} is scheduled before {{earlier}}" verbatim at the user.
           * The container's own page never had the bug because it keys and
           * interpolates in the same breath.
           *
           * Translating early is safe: `translateValidation` passes anything
           * that is not a `validation:` key straight through, so the form's own
           * call on the way to the field leaves the finished sentence alone.
           */
          message: translateValidation(violation.messageKey, violation.values),
        });
      });

      if (!failures.length) return true;
      return failures.length === 1
        ? failures[0]
        : new Yup.ValidationError(failures);
    }),

  /**
   * The two plate figures the order hands down to every container it generates.
   *
   * Both stay optional — an order is routinely booked before anyone has seen a
   * door plate — and either one alone is accepted. But when both are given they
   * have to be the right way round: no freight container carries less than it
   * weighs, so a tare above the payload rating is two figures swapped, not an
   * unusual box.
   *
   * Enforced here because these values are the *defaults* a container line is
   * seeded from, and the line is held to the same rule (`tareOverPayload` in
   * ContainerOrderLine) — so an order saved the wrong way round produced
   * containers that refused to be created, complaining about figures nobody had
   * typed on that screen. Reported on the payload field, the same side the
   * container's own check reports on.
   *
   * Guarded on positive, not merely present: a cleared number input reads as
   * NaN and zero means "nobody entered this" everywhere these weights are
   * handled, so neither may be accused of being out of order.
   */
  defaultMaxPayloadKg: Yup.mixed()
    // Explicit, because yup schemas are non-nullable by default: the form seeds
    // both weights as null, so without this an empty payload box was refused
    // with yup's own "cannot be null" before the rule below was ever consulted.
    .nullable()
    .notRequired()
    .test(
      "payload-below-tare",
      "validation:containerOrder.tareOverPayload",
      function (value) {
        const payload = Number(value);
        const tare = Number(this.parent?.defaultTareKg);
        if (!Number.isFinite(payload) || payload <= 0) return true;
        if (!Number.isFinite(tare) || tare <= 0) return true;
        return tare <= payload;
      },
    ),

  // Optional, but if both ends of the range are given they must be in order.
  dateTo: Yup.string().test(
    "range-order",
    "validation:containerOrder.dateRangeInvalid",
    function (value) {
      const from = this.parent?.dateFrom;
      if (!value || !from) return true;
      return new Date(value) >= new Date(from);
    },
  ),
});
